/**
 * Base store class for frontend state management
 * Implements a simple observable store pattern with subscriptions
 */

export type Listener<T> = (state: T) => void;
export type Unsubscribe = () => void;
export type Selector<T, R> = (state: T) => R;
export type Comparator<R> = (prev: R, next: R) => boolean;

/**
 * Base store configuration options
 */
export interface StoreOptions<T> {
  /** Storage key for persistence (optional) */
  persistKey?: string;
  /** Custom serializer for persistence */
  serialize?: (state: T) => string;
  /** Custom deserializer for persistence */
  deserialize?: (raw: string) => T;
  /** Whether to use sessionStorage instead of localStorage */
  useSessionStorage?: boolean;
}

/**
 * Generic store class with subscription support
 * @template T - The shape of the state
 */
export class Store<T extends object> {
  private state: T;
  private listeners: Set<Listener<T>> = new Set();
  private selectorListeners: Map<Listener<unknown>, {
    selector: Selector<T, unknown>;
    comparator: Comparator<unknown>;
    lastValue: unknown;
  }> = new Map();
  private readonly options: StoreOptions<T>;

  constructor(initialState: T, options: StoreOptions<T> = {}) {
    this.options = options;
    this.state = this.loadPersistedState() ?? initialState;
  }

  /**
   * Get the current state (readonly)
   */
  getState(): Readonly<T> {
    return this.state;
  }

  /**
   * Update state with partial update (merged with current state)
   */
  setState(partial: Partial<T>): void {
    const prevState = this.state;
    this.state = { ...prevState, ...partial };
    this.persistState();
    this.notifyListeners();
    this.notifySelectorListeners();
  }

  /**
   * Replace entire state (useful for reset operations)
   */
  replaceState(newState: T): void {
    this.state = newState;
    this.persistState();
    this.notifyListeners();
    this.notifySelectorListeners();
  }

  /**
   * Subscribe to all state changes
   * @returns Unsubscribe function
   */
  subscribe(listener: Listener<T>): Unsubscribe {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Subscribe to selected/computed state changes
   * Only notifies when the selected value changes
   * @param selector - Function to select/compute a value from state
   * @param listener - Callback when selected value changes
   * @param comparator - Optional custom comparator (default: shallow equality)
   * @returns Unsubscribe function
   */
  subscribeToSelector<R>(
    selector: Selector<T, R>,
    listener: Listener<R>,
    comparator: Comparator<R> = (prev, next) => prev === next
  ): Unsubscribe {
    const currentValue = selector(this.state);

    // Store the listener with its selector and last value
    this.selectorListeners.set(listener as Listener<unknown>, {
      selector: selector as Selector<T, unknown>,
      comparator: comparator as Comparator<unknown>,
      lastValue: currentValue,
    });

    // Immediately call with current value
    listener(currentValue);

    return () => {
      this.selectorListeners.delete(listener as Listener<unknown>);
    };
  }

  /**
   * Create a derived/computed value from the state
   * Returns a function that computes the value on demand
   */
  computed<R>(selector: Selector<T, R>): () => R {
    return () => selector(this.state);
  }

  /**
   * Reset state to initial value and clear persistence
   */
  reset(initialState: T): void {
    this.clearPersistedState();
    this.replaceState(initialState);
  }

  /**
   * Get number of active subscribers (useful for debugging)
   */
  getSubscriberCount(): number {
    return this.listeners.size + this.selectorListeners.size;
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.state);
      } catch (error) {
        console.error('Error in store listener:', error);
      }
    });
  }

  private notifySelectorListeners(): void {
    this.selectorListeners.forEach((config, listener) => {
      try {
        const newValue = config.selector(this.state);
        if (!config.comparator(config.lastValue, newValue)) {
          config.lastValue = newValue;
          listener(newValue);
        }
      } catch (error) {
        console.error('Error in store selector listener:', error);
      }
    });
  }

  private getStorage(): Storage | null {
    if (!this.options.persistKey) return null;
    try {
      return this.options.useSessionStorage ? sessionStorage : localStorage;
    } catch {
      // Storage not available (e.g., private browsing)
      return null;
    }
  }

  private persistState(): void {
    const storage = this.getStorage();
    if (!storage || !this.options.persistKey) return;

    try {
      const serializer = this.options.serialize ?? JSON.stringify;
      storage.setItem(this.options.persistKey, serializer(this.state));
    } catch (error) {
      console.warn('Failed to persist state:', error);
    }
  }

  private loadPersistedState(): T | null {
    const storage = this.getStorage();
    if (!storage || !this.options.persistKey) return null;

    try {
      const raw = storage.getItem(this.options.persistKey);
      if (!raw) return null;

      const deserializer = this.options.deserialize ?? JSON.parse;
      return deserializer(raw) as T;
    } catch (error) {
      console.warn('Failed to load persisted state:', error);
      return null;
    }
  }

  private clearPersistedState(): void {
    const storage = this.getStorage();
    if (!storage || !this.options.persistKey) return;

    try {
      storage.removeItem(this.options.persistKey);
    } catch (error) {
      console.warn('Failed to clear persisted state:', error);
    }
  }
}

/**
 * Create a simple store instance
 * @param initialState - Initial state object
 * @param options - Store configuration options
 */
export function createStore<T extends object>(
  initialState: T,
  options: StoreOptions<T> = {}
): Store<T> {
  return new Store(initialState, options);
}

/**
 * Helper to create a memoized selector with single dependency
 * Caches the result until the dependency changes
 */
export function createSelector<T, D, R>(
  dependency: Selector<T, D>,
  combiner: (dep: D) => R
): Selector<T, R>;

/**
 * Helper to create a memoized selector with two dependencies
 */
export function createSelector<T, D1, D2, R>(
  dependencies: [Selector<T, D1>, Selector<T, D2>],
  combiner: (d1: D1, d2: D2) => R
): Selector<T, R>;

/**
 * Helper to create a memoized selector with three dependencies
 */
export function createSelector<T, D1, D2, D3, R>(
  dependencies: [Selector<T, D1>, Selector<T, D2>, Selector<T, D3>],
  combiner: (d1: D1, d2: D2, d3: D3) => R
): Selector<T, R>;

/**
 * Implementation of createSelector
 */
export function createSelector<T, R>(
  dependencyOrDependencies: Selector<T, unknown> | Selector<T, unknown>[],
  combiner: (...deps: unknown[]) => R
): Selector<T, R> {
  const dependencies = Array.isArray(dependencyOrDependencies)
    ? dependencyOrDependencies
    : [dependencyOrDependencies];
  let lastDeps: unknown[] | null = null;
  let lastResult: R;

  return (state: T): R => {
    const newDeps = dependencies.map((dep) => dep(state));

    if (lastDeps !== null && newDeps.length === lastDeps.length &&
        newDeps.every((dep, i) => dep === lastDeps![i])) {
      return lastResult;
    }

    lastDeps = newDeps;
    lastResult = combiner(...newDeps);
    return lastResult;
  };
}

/**
 * Deep equality comparator for use with subscribeToSelector
 */
export function deepEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) {
      return false;
    }
  }

  return true;
}
